import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';

const CookiePolicyPage = () => {
  const openCookiePreferences = () => {
    // Call the global method to open the cookie preferences dialog
    if (typeof window.openCookiePreferences === 'function') {
      window.openCookiePreferences();
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h4" gutterBottom>Cookie Policy</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SettingsIcon />}
            onClick={openCookiePreferences}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              boxShadow: 2
            }}
          >
            Adjust Cookie Settings
          </Button>
        </Box>
        <Typography variant="subtitle1" color="text.secondary" paragraph>
          Last updated: March 24, 2025
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>What Are Cookies</Typography>
        <Typography paragraph>
          Cookies are small text files that are stored on your computer or mobile device when you visit a website.
          They are widely used to make websites work more efficiently and provide information to the website owners.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>How We Use Cookies</Typography>
        <Typography paragraph>
          At Hive B Ltd, we use cookies to enhance your experience on our coworking space brokerage platform.
          Our cookies help us authenticate users, remember your preferences, enable the functionality of our
          payment systems, and provide location-based services. All data processing occurs within the United Kingdom.
        </Typography>

        <TableContainer component={Paper} variant="outlined" sx={{ my: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell><strong>Purpose</strong></TableCell>
                <TableCell><strong>Examples</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Essential</TableCell>
                <TableCell>These cookies are necessary for the website to function properly and cannot be switched off in our systems.</TableCell>
                <TableCell>Session cookies, authentication tokens, CSRF tokens</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Authentication</TableCell>
                <TableCell>These cookies help us identify you when you&apos;re logged in so we can show you appropriate content and features.</TableCell>
                <TableCell>Google authentication cookies (SIDCC, HSID, SID, etc.)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Functionality</TableCell>
                <TableCell>These cookies enable the website to provide enhanced functionality and personalisation based on your interactions.</TableCell>
                <TableCell>User preference cookies, language settings</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Payment Processing</TableCell>
                <TableCell>These cookies are used to enable secure payments through our platform.</TableCell>
                <TableCell>Stripe cookies (site-auth, merchant, stripe.csrf)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Location Based</TableCell>
                <TableCell>These cookies help us provide location-based services for finding nearby coworking spaces.</TableCell>
                <TableCell>Google Maps cookies</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Analytics</TableCell>
                <TableCell>These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site.</TableCell>
                <TableCell>Google Analytics cookies (_ga, _gid, _gat)</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Specific Cookies We Use</Typography>

        <Typography variant="subtitle1" sx={{ mt: 3 }}>Google Analytics Cookies</Typography>
        <Typography paragraph>
          We use Google Analytics to understand how visitors interact with our website. This helps us improve our platform and provide a better user experience.
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ my: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Cookie Name</strong></TableCell>
                <TableCell><strong>Purpose</strong></TableCell>
                <TableCell><strong>Duration</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>_ga</TableCell>
                <TableCell>Used to distinguish users</TableCell>
                <TableCell>2 years</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>_gid</TableCell>
                <TableCell>Used to distinguish users</TableCell>
                <TableCell>24 hours</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>_gat</TableCell>
                <TableCell>Used to throttle request rate</TableCell>
                <TableCell>1 minute</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>_ga_[site-identifier]</TableCell>
                <TableCell>Used to persist session state</TableCell>
                <TableCell>2 years</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Typography paragraph>
          Google Analytics data is processed in accordance with Google's privacy policy. Google may process this data in countries outside the European Economic Area. You can learn more about how Google uses information from sites that use their services at: <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">https://policies.google.com/technologies/partner-sites</a>
        </Typography>

        <Typography paragraph>
          You can opt out of Google Analytics tracking by installing the Google Analytics Opt-out Browser Add-on, available at: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">https://tools.google.com/dlpage/gaoptout</a>
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 2 }}>Google Authentication Cookies</Typography>
        <Typography paragraph>
          We use Google&apos;s authentication services to provide secure login options. These cookies help verify your identity and
          maintain your session across our platform.
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ my: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Cookie Name</strong></TableCell>
                <TableCell><strong>Purpose</strong></TableCell>
                <TableCell><strong>Duration</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>SIDCC, __Secure-1PSIDCC, __Secure-3PSIDCC</TableCell>
                <TableCell>Security cookies to protect user data from unauthorised access</TableCell>
                <TableCell>1 year</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>SID, HSID, SSID</TableCell>
                <TableCell>Contain digitally signed and encrypted records of a user&apos;s Google Account ID</TableCell>
                <TableCell>2 years</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>APISID, SAPISID</TableCell>
                <TableCell>Used to build a profile of your interests and show you relevant ads</TableCell>
                <TableCell>2 years</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>__Secure-1PAPISID, __Secure-3PAPISID</TableCell>
                <TableCell>Secure versions of the APISID cookie</TableCell>
                <TableCell>2 years</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="subtitle1" sx={{ mt: 3 }}>Stripe Payment Cookies</Typography>
        <Typography paragraph>
          We use Stripe for secure payment processing. These cookies are essential for completing transactions on our platform.
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ my: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Cookie Name</strong></TableCell>
                <TableCell><strong>Purpose</strong></TableCell>
                <TableCell><strong>Duration</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>stripe.csrf</TableCell>
                <TableCell>Helps prevent cross-site request forgery (CSRF) attacks</TableCell>
                <TableCell>1 year</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>site-auth</TableCell>
                <TableCell>Used for authentication within Stripe&apos;s payment system</TableCell>
                <TableCell>Session</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>__stripe_mid, __stripe_orig_props</TableCell>
                <TableCell>Used for fraud prevention and detection</TableCell>
                <TableCell>1 year</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>merchant</TableCell>
                <TableCell>Stores merchant information for payment processing</TableCell>
                <TableCell>Session</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="subtitle1" sx={{ mt: 3 }}>Platform-Specific Cookies</Typography>
        <Typography paragraph>
          We use several cookies specific to our platform to enhance your experience and maintain your preferences.
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ my: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Cookie Name</strong></TableCell>
                <TableCell><strong>Purpose</strong></TableCell>
                <TableCell><strong>Duration</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>sessionid</TableCell>
                <TableCell>Maintains your session state across page requests</TableCell>
                <TableCell>Session</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>csrftoken</TableCell>
                <TableCell>Helps protect against Cross-Site Request Forgery attacks</TableCell>
                <TableCell>1 year</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>cookie-prefs</TableCell>
                <TableCell>Stores your cookie preferences</TableCell>
                <TableCell>1 year</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Your Cookie Choices</Typography>
        <Typography paragraph>
          When you first visit our site, you will be shown a cookie banner where you can choose which types of cookies you want to accept. You can manage your preferences at any time using the &quot;Adjust Cookie Settings&quot; button at the top of this page or the &quot;Cookie Settings&quot; link in the footer of our website.
        </Typography>
        <Typography paragraph>
          Please note that essential cookies cannot be disabled as they are necessary for the website to function properly.
        </Typography>

        <Box sx={{ my: 4, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<SettingsIcon />}
            onClick={openCookiePreferences}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              boxShadow: 2,
              px: 4
            }}
          >
            Manage Cookie Preferences
          </Button>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>How to Control Cookies</Typography>
        <Typography paragraph>
          Most web browsers allow you to control cookies through their settings. You can:
        </Typography>
        <ul>
          <li>
            <Typography paragraph>
              Delete cookies from your device
            </Typography>
          </li>
          <li>
            <Typography paragraph>
              Block cookies by activating the setting in your browser
            </Typography>
          </li>
          <li>
            <Typography paragraph>
              Set your browser to notify you when you receive a cookie
            </Typography>
          </li>
        </ul>
        <Typography paragraph>
          Please note that if you choose to block certain cookies, you may not be able to use all the features of our platform.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Data Storage and Security</Typography>
        <Typography paragraph>
          We prioritize the security of your data. While we use cookies to enhance your experience, we only store essential user information within our platform. Payment data is processed and stored securely by Stripe, and we only maintain tokens for reference purposes. All data processing occurs within the United Kingdom.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Updates to This Policy</Typography>
        <Typography paragraph>
          We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We encourage you to periodically review this page for the latest information on our cookie practices.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Contact Us</Typography>
        <Typography paragraph>
          If you have any questions about our use of cookies, please contact us at{' '}
          <Box component="span" sx={{ fontWeight: 'bold' }}>hello@hivespace.co</Box>
        </Typography>
      </Paper>
    </Container>
  );
};

export default CookiePolicyPage;